// @flow
import React from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { track } from 'app/common/lib/react/analytics';
import { compose, type Dispatch } from 'redux';
import { Icon, SegmentedControl } from 'gestalt';
import { updateBoardPinDensity, updateProfilePinDensity } from 'app/common/redux/actions/views';

type OwnProps = {|
  viewType: $Values<PCONST.thrift.ViewType>,
  component: $Values<PCONST.thrift.ComponentType>,
  metadata?: {
    data?: {
      profile_pin_rep_view_type: null | 'regular' | 'dense',
      board_pin_rep_view_type: null | 'regular' | 'dense',
    },
  },
  pinView: 'board' | 'profile',
|};

type ReduxProps = {|
  updatedBoardPinDensity: '' | 'regular' | 'dense',
|};

type Props = {|
  ...OwnProps,
  ...ReduxProps,
  dispatch: Dispatch<any>,
|};

export class PinRepViewSelector extends React.Component<Props> {
  componentDidUpdate() {
    const { dispatch, metadata, pinView, updatedBoardPinDensity } = this.props;
    if (metadata && metadata.data && updatedBoardPinDensity === '') {
      const {
        profile_pin_rep_view_type: profilePinRepViewType,
        board_pin_rep_view_type: boardPinRepViewType,
      } = metadata.data;

      // NOTE(allenk): The naming here is pretty unfortunate. When I was originally going
      // through and building this, I named the key for the board page "profile_...".
      // Now that we're experimenting with adding this to the pins tab, I'm going to keep the
      // naming for now, since otherwise we'd lose a user's saved preference.

      // TODO(allenk): Once the profile pins tab toggle ships, we can go ahead and flip these
      // keys.
      if (pinView === 'board') {
        if (!!profilePinRepViewType) {
          dispatch(updateBoardPinDensity(profilePinRepViewType));
        } else {
          dispatch(updateBoardPinDensity('regular'));
        }
      }

      if (pinView === 'profile') {
        if (!!boardPinRepViewType) {
          dispatch(updateProfilePinDensity(boardPinRepViewType));
        } else {
          dispatch(updateProfilePinDensity('regular'));
        }
      }
    }
  }

  getElementType(boardDensity: 'regular' | 'dense') {
    const { viewType } = this.props;
    switch (viewType) {
      case PCONST.thrift.ViewType.BOARD_SECTION:
        return boardDensity === 'regular'
          ? PCONST.thrift.ElementType.BOARD_SECTION_PIN_REP_VIEW_DEFAULT_OPTION
          : PCONST.thrift.ElementType.BOARD_SECTION_PIN_REP_VIEW_DENSE_OPTION;
      case PCONST.thrift.ViewType.BOARD:
        return boardDensity === 'regular'
          ? PCONST.thrift.ElementType.BOARD_PIN_REP_VIEW_DEFAULT_OPTION
          : PCONST.thrift.ElementType.BOARD_PIN_REP_VIEW_DENSE_OPTION;
      case PCONST.thrift.ViewType.USER:
        return boardDensity === 'regular'
          ? PCONST.thrift.ElementType.PROFILE_PIN_REP_VIEW_DEFAULT_OPTION
          : PCONST.thrift.ElementType.PROFILE_PIN_REP_VIEW_DENSE_OPTION;
      default:
        return PCONST.thrift.ElementType.BOARD_PIN_REP_VIEW_DEFAULT_OPTION;
    }
  }

  icons = {
    regular: {
      path:
        'M13.00,20.00 L13.00,13.00 L20.00,13.00 L20.00,20.00 L13.00,20.00 Z M4.00,11.00 L4.00,4.00 L11.00,4.00 L11.00,11.00 L4.00,11.00 Z M13.00,11.00 L13.00,4.00 L20.00,4.00 L20.00,11.00 L13.00,11.00 Z M4.00,20.00 L4.00,13.00 L11.00,13.00 L11.00,20.00 L4.00,20.00 Z',
      label: i18n._('Regular', 'Label for selecting the regular pin density view'),
    },
    dense: {
      path:
        'M16.00,20.00 L20.00,20.00 L20.00,16.00 L16.00,16.00 L16.00,20.00 Z M10.00,8.00 L10.00,4.00 L14.00,4.00 L14.00,8.00 L10.00,8.00 Z M4.00,8.00 L4.00,4.00 L8.00,4.00 L8.00,8.00 L4.00,8.00 Z M16.00,8.00 L16.00,4.00 L20.00,4.00 L20.00,8.00 L16.00,8.00 Z M4.00,14.00 L4.00,10.00 L8.00,10.00 L8.00,14.00 L4.00,14.00 Z M10.00,14.00 L10.00,10.00 L14.00,10.00 L14.00,14.00 L10.00,14.00 Z M16.00,14.00 L16.00,10.00 L20.00,10.00 L20.00,14.00 L16.00,14.00 Z M4.00,20.00 L4.00,16.00 L8.00,16.00 L8.00,20.00 L4.00,20.00 Z M10.00,20.00 L10.00,16.00 L14.00,16.00 L14.00,20.00 L10.00,20.00 Z',
      label: i18n._('Dense', 'Label for selecting the dense pin density view'),
    },
  };

  handleItemChange = ({ activeIndex }: Object) => {
    const { dispatch, viewType: view, component, pinView } = this.props;
    const boardDensity = activeIndex === 0 ? 'regular' : 'dense';

    const element = this.getElementType(boardDensity);

    const viewParameter =
      view === PCONST.thrift.ViewType.USER
        ? PCONST.thrift.ViewParameterType.USER_SELF
        : PCONST.thrift.ViewParameterType.BOARD_SELF;

    track(PCONST.thrift.EventType.CLICK, {
      view,
      component,
      element,
      viewParameter,
    });

    const updatedData =
      pinView === 'board'
        ? { profile_pin_rep_view_type: boardDensity }
        : { board_pin_rep_view_type: boardDensity };
    const dispatchFunc = pinView === 'board' ? updateBoardPinDensity : updateProfilePinDensity;

    const resource = ResourceFactory.create('UserMetadataResource', updatedData);
    resource.callUpdate();

    dispatch(dispatchFunc(boardDensity));
  };

  render() {
    const { metadata, updatedBoardPinDensity } = this.props;

    if (!metadata || !metadata.data) {
      return null;
    }

    const selectedItemIndex = updatedBoardPinDensity === 'dense' ? 1 : 0;

    return (
      <div className="OwnBoardSelector">
        {/* deliberately adding <div /> for printing boards */}
        <SegmentedControl
          items={Object.keys(this.icons).map((icon, index) => {
            const { label, path } = this.icons[icon];
            return (
              <Icon
                accessibilityLabel={label}
                dangerouslySetSvgPath={{ __path: path }}
                color={selectedItemIndex === index ? 'darkGray' : 'gray'}
                size={24}
              />
            );
          })}
          onChange={this.handleItemChange}
          selectedItemIndex={selectedItemIndex}
          responsive
        />
      </div>
    );
  }
}

export default compose(
  connect<_, OwnProps, _, _, _, _>((state: ReduxState, props: OwnProps) => ({
    updatedBoardPinDensity: state.ui.views && state.ui.views[`${props.pinView}PinView`],
  })),
  withViewer,
  withResource(
    {
      name: 'UserMetadataResource',
      key: 'metadata',
      options: (props) => ({
        user: {
          id: props.viewer.username,
        },
      }),
    },
    { allowStale: false },
  ),
)(PinRepViewSelector);
